import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from "@angular/core";
import { FormGroup, Validators } from "@angular/forms";
import { Subject } from "rxjs";
import { debounceTime, distinctUntilChanged, tap } from "rxjs/operators";
import { SubSink } from "subsink";
import { MessagingService } from "../../../../../core/messaging/messaging.service";
import { SeverityType } from "../../../../../core/messaging/severity-type.enum";
import { FormService } from "../../../../../dynamic-forms/form.service";
import { ChipAutocomplete } from "../../../../../dynamic-forms/inputs/chip-autocomplete/chip-autocomplete.model";
import { DynamicInput } from "../../../../../dynamic-forms/inputs/dynamic-input.model";
import { SelectableInput } from "../../../../../dynamic-forms/inputs/selectable-input.model";
import { TagSearchAutoComplete } from "../../../../../dynamic-forms/inputs/tag-search-autocomplete/tag-search.model";
import { TagType } from "../../../../../shared/tags/model/tag-type.enum";
import { TagService } from "../../../../../shared/tags/tag.service";
import { ArrayHelper } from "../../../../../utilities/contracts/array-helper";
import { StringHelper } from "../../../../../utilities/contracts/string-helper";
import { RegExHelper } from "../../../../../utilities/reg-Ex-Helper";

@Component({
  selector: "form-bulk-update-manage-tag",
  templateUrl: "./bulk-update-manage-tag.component.html",
  styleUrls: ["./bulk-update-manage-tag.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BulkUpdateManageTagComponent implements OnInit, OnChanges, OnDestroy {
  tagFormGroup: FormGroup;
  addTagInput: ChipAutocomplete;
  tagInput: TagSearchAutoComplete;
  replaceTagInput: TagSearchAutoComplete;
  removeTagInput: TagSearchAutoComplete;
  @Input() tagInputActive = false;
  isShowSearchIcon = true;
  @Input() tagType: TagType;
  @Input() tagActionType = "";
  addTag = false;
  removeTag = false;
  replaceTag = false;
  selectedTags: any;
  tagName = "";
  pTagText = "";
  tagId: number;
  replaceTagId: number;
  lastInputSearch: string;
  sink = new SubSink();
  @Input() isCreateTagTemplateVisible: boolean;
  isVisible = false;
  @Output() tagDataModel: EventEmitter<any> = new EventEmitter();
  @Output() hasManageTagType: EventEmitter<boolean> = new EventEmitter(false);
  @Output() addTagValid: EventEmitter<boolean> = new EventEmitter(false);
  @Input() showAddTagValidationError = false;
  searchTextChanged = new Subject<string>();
  searchInputName: string;

  constructor(
    private readonly formService: FormService,
    private readonly tagService: TagService,
    private readonly changeDetector: ChangeDetectorRef,
    private messagingService: MessagingService,
    private readonly el: ElementRef,
    private readonly re: ElementRef
  ) { }

  ngOnInit() {
    this.initializeTagForm();
    this.sink.add(
      this.searchTextChanged
          .pipe(
          debounceTime(500),
          distinctUntilChanged(),
          tap(searchText => this.getTagsList(searchText))
          ).subscribe()
      );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes && changes.tagActionType) {
      this.getTagAction();
    }
  }

  get hasCurrentTag(): boolean {
    return this.addTag && this.replaceTag;
  }

  get hasAddTag(): boolean {
    return this.addTag && !this.removeTag && !this.replaceTag;
  }

  get isTagUnAvailable(): boolean {
    return !ArrayHelper.isAvailable(this.selectedTags) && !this.isVisible;
  }

  get isTagAvailable(): boolean {
    return ArrayHelper.isAvailable(this.selectedTags);
  }

  get lowerBoundClass(): string {
    return (this.el.nativeElement.getBoundingClientRect().right > (window.innerWidth - 420)) ? "flip-dropdown" : "";
  }

  get hasValidationError(): boolean {
    return this.showAddTagValidationError && !ArrayHelper.isAvailable(this.selectedTags);
  }

  @HostListener("document:click", ["$event", "$event.target"])
  onClick(event: MouseEvent, targetElement: HTMLElement): void {
    if (!targetElement) {
      return;
    }
    const clickedInside = targetElement.id === "addTags" || targetElement.className === "far fa-plus-circle fa-far fa-plus-circle"
      || targetElement.className === "color-picker__tag-info--header";
    this.isVisible = !clickedInside ? false : true;
    if (this.selectedTags) {
      if (targetElement.className === "p-chips-token-label") {
        this.selectedTags = this.selectedTags.map(x => {
          x.extra.isSelected = true;
          return x;
        });
      } else {
        this.selectedTags = this.selectedTags.map(x => {
          x.extra.isSelected = false;
          return x;
        });
      }
    }
  }

  private initializeTagForm(): void {
    this.addTagInput =
      new ChipAutocomplete({
        key: "addTags",
        placeholder: "Type Here",
        label: "addTags",
        validators: [
          Validators.maxLength(50),
          Validators.pattern(RegExHelper.tagPattern)],
        errorMessages: {
          maxlength: "Tag text cannot be more than 50 characters.",
          pattern: "Tags Cannot Contain Special Characters.",
        },
        useFilter: false,
      });
    this.tagInput =
      new TagSearchAutoComplete({
        key: "tags",
        placeholder: "Type Here",
      });
    this.replaceTagInput =
      new TagSearchAutoComplete({
        key: "replaceTags",
        placeholder: "Type Here",
      });
    this.removeTagInput =
      new TagSearchAutoComplete({
        key: "removeTags",
        placeholder: "Type Here",
      });
    this.tagFormGroup = this.formService.createFormGroup([this.addTagInput, this.tagInput, this.replaceTagInput, this.removeTagInput]);
    this.changeDetector.markForCheck();
  }

  onTagInputKeyUp(event): void {
    this.searchInputName = this.tagInput.key;
    this.searchTextChanged.next(event.target.value);
  }

  onAddTagInputKeyUp(event): void {
    this.searchInputName = this.addTagInput.key;
    this.searchTextChanged.next(event.target.value);
  }

  onReplaceTagInputKeyUp(event): void {
    this.searchInputName = this.replaceTagInput.key;
    this.searchTextChanged.next(event.target.value);
  }

  onRemoveTagInputKeyUp(event): void {
    this.searchInputName = this.removeTagInput.key;
    this.searchTextChanged.next(event.target.value);
  }

  private getTagsList(searchText: string): void {
    this.tagService.getAllTagsList(this.tagType, null, searchText).subscribe(result => {
      result.sort((a, b) => a.text.localeCompare(b.text));

      switch (this.searchInputName) {

        case this.addTagInput.key:
          this.addTagInput.options = [...result];
          break;
        case this.replaceTagInput.key:
          this.replaceTagInput.options = [...result];
          break;
        case this.tagInput.key:
          this.tagInput.options = [...result];
          break;
        case this.removeTagInput.key:
          this.removeTagInput.options = [...result];
          break;
        default:
          break;
      }
      this.changeDetector.markForCheck();
    });
  }

  private getTagAction(): void {
    if (this.tagFormGroup) {
      this.selectedTags = [];
      this.isVisible = false;
      this.pTagText = "";
      this.tagFormGroup.reset();
    }
    switch (this.tagActionType) {
      case "Add":
        this.addTag = true;
        this.removeTag = false;
        this.replaceTag = false;
        break;
      case "Remove":
        this.addTag = false;
        this.removeTag = true;
        this.replaceTag = false;
        break;
      case "Replace":
        this.addTag = true;
        this.removeTag = false;
        this.replaceTag = true;
        break;
      default:
        break;
    }
  }

  onTagInput(event: any): void {
    if (event.value) {
      this.tagId = event.value;
      this.tagDataModel.emit({
        tagId: this.tagId,
      });
    } else if (event.target.value) {
      const searchTextValue = this.tagInput.options.find(x => x.text.toLowerCase() === event.target.value.toLowerCase());
      this.setTagValue(searchTextValue, "currentTag");
    }
  }

  onReplaceTagInput(event: any): void {
    if (event.value) {
      this.replaceTagId = event.value;
      this.tagDataModel.emit({
        tagId: this.tagId,
        replaceTagId: this.replaceTagId,
      });
    } else if (event.target.value) {
      const searchTextValue = this.removeTagInput.options.find(x => x.text.toLowerCase() === event.target.value.toLowerCase());
      this.setTagValue(searchTextValue, "replaceTag");
    }
  }


  private setTagValue(searchText: SelectableInput, tagControl: string): void {
    if (searchText) {
      let tagValue: SelectableInput;
      tagValue = new SelectableInput({ text: searchText.text, value: 0 });
      if (tagControl === "currentTag") {
        this.tagFormGroup.get(this.tagInput.key).setValue(tagValue);
        this.tagFormGroup.get(this.removeTagInput.key).setValue(tagValue);
      } else {
        this.tagFormGroup.get(this.replaceTagInput.key).setValue(tagValue);
      }
    }
  }

  onTagEnter(): void {
    this.clearValidators([this.removeTagInput.key, this.replaceTagInput.key]);
    if (StringHelper.isAvailable(this.pTagText)) {
      if (this.tagFormGroup.invalid) {
        this.messagingService.showToast("Tags Cannot Contain Special Characters or cannot be more than 50 characters.", SeverityType.ERROR);
        return;
      }
      this.tagName = this.pTagText;
      const defaultColor = "#DCDCDC";
      if (StringHelper.isAvailable(this.pTagText)) {
        const newTag = new SelectableInput({
          text: this.tagName.toUpperCase(),
          value: 0,
          extra: { color: defaultColor },
        });
        this.addTagToExistingTagList(newTag);
      }
    }
  }

  onSelectedColor(selectedTagColor: string): void {
    if (StringHelper.isAvailable(selectedTagColor) && StringHelper.isAvailable(this.pTagText)) {
      if (this.tagFormGroup.invalid) {
        this.messagingService.showToast("Tags Cannot Contain Special Characters or cannot be more than 50 characters.", SeverityType.ERROR);
        return;
      }
      this.tagName = this.pTagText.toUpperCase();
      const newTag = new SelectableInput({
        text: this.tagName,
        value: 0,
        extra: { color: selectedTagColor },
      });
      this.addTagToExistingTagList(newTag);
    } else {
      this.isCreateTagTemplateVisible = false;
    }
  }

  onAddTags(event: any): void {
    this.clearValidators([this.removeTagInput.key, this.replaceTagInput.key]);
    this.isCreateTagTemplateVisible = true;
    if (event.text) {
      this.pTagText = event.text;
      this.addTagToExistingTagList(event);
    } else {
      this.pTagText = event.target.value;
      if (!this.tagFormGroup.valid) {
        this.formService.markAllAsTouched(this.tagFormGroup);
        this.changeDetector.markForCheck();
      }
    }
  }

  addTagToExistingTagList(item: SelectableInput): void {
    this.selectedTags = this.selectedTags || [];
    if (this.selectedTags.length >= 8) {
      this.messagingService.showToast("Max number of tags reached.", SeverityType.ERROR);
    } else {
      if (item) {
        const isTagAlreadyAvailable = this.selectedTags.filter(x => x.text === item.text);
        if (isTagAlreadyAvailable.length === 0) {
          this.selectedTags.push(item);
          this.tagDataModel.emit({
            addedTagList: this.selectedTags,
          });
          this.selectedTags.map(x => x.extra.isSelected = false);
        } else {
          this.messagingService.showToast("Tag already Selected.", SeverityType.ERROR);
        }
      }
    }
    this.isCreateTagTemplateVisible = false;
    this.tagFormGroup.reset();
  }

  getCharactersCount(selectedTags: string): string {
    return selectedTags.length > 12 ? selectedTags.toUpperCase() : "";
  }

  onItemClick(): void {
    this.selectedTags = this.selectedTags.map(x => {
      x.extra.isSelected = true;
      return x;
    });
  }

  getTagIcon(iconName: string): string {
    return iconName === "add" ? "far fa-plus-circle" : "p-chips-token-icon pi pi-fw pi-times";
  }

  getClassForPlusIcon(): string {
    const styleClass = "tag-icon";
    return this.isVisible ? "tag-unclickable" : styleClass;
  }

  getClassForNewTagIcon(): string {
    const iconClass = "tag-list__add-icon";
    return iconClass;
  }

  getClassForSelect(): string {
    let iconStyle = "";
    if (this.selectedTags && this.isVisible) {
      iconStyle = "tag-select";
    } else if (!this.selectedTags && this.isVisible) {
      iconStyle = "tag-select-item";
    }
    return iconStyle;
  }

  addTags(selectedTags: boolean): void {
    this.isVisible = selectedTags;
    this.pTagText = "";
    this.tagFormGroup.get(this.addTagInput.key).setValue("");
  }

  removeItem(index: number): void {
    this.selectedTags = this.selectedTags.filter((val, i) => i !== index);
    this.tagDataModel.emit({
      addedTagList: this.selectedTags,
    });
  }

  setTagFormGroupValidation() {
    if (this.hasManageTagType) {
      switch (this.tagActionType) {
        case "Remove":
          this.removeTagInput = new TagSearchAutoComplete({
            ...this.removeTagInput,
            validators: [
              Validators.required,
            ],
            errorMessages: {
              required: "Remove Tag is required",
            },
          } as any);
          this.setValidators([this.removeTagInput]);
          break;
        case "Replace":
          this.replaceTagInput = new TagSearchAutoComplete({
            ...this.replaceTagInput,
            validators: [
              Validators.required,
            ],
            errorMessages: {
              required: "Replacement Tag is required",
            },
          } as any);
          this.tagInput = new TagSearchAutoComplete({
            ...this.tagInput,
            validators: [
              Validators.required,
            ],
            errorMessages: {
              required: "Current Tag is required",
            },
          } as any);
          this.setValidators([this.replaceTagInput, this.tagInput]);
          break;
        default:
          break;
      }
    } else {
      this.clearValidators([this.removeTagInput.key, this.replaceTagInput.key, this.tagInput.key]);
    }
  }

  private setValidators(controls: DynamicInput[]): void {
    controls.forEach(control => {
      this.tagFormGroup.get(control.key).setValidators(control.validators);
    });
  }

  private clearValidators(path: string[]): void {
    path.forEach(control => {
      this.tagFormGroup.get(control).clearValidators();
      this.tagFormGroup.get(control).updateValueAndValidity();
    });
  }

  setAddTagFormValidation(isInValid: boolean): void {
    const isAddTagAvailable = isInValid && this.tagActionType === "Add" && !ArrayHelper.isAvailable(this.selectedTags);
    this.addTagValid.emit(isAddTagAvailable);
  }

  ngOnDestroy(): void {
    this.sink.unsubscribe();
  }

}
